// Desktop hover (nowish.json hover; docs/PARITY.md: phones have no hover; port of web/hover.js). Hovering a pin or a
// group, or an event's card in the side panel, picks it out: the pin (or group) grows to hover.pinScale at full
// strength, its card gets an outline in its time tone (green now, else blue) and scrolls into view if it's off screen
// (from a map pin only), and every other pin, group, off-screen pointer and card (cards: not for a map group) goes to
// hover.dimOthers of its usual opacity. Cheap at any number of events: one paint expression per layer (stepped over
// hover.ms), the grown pin is one HTML marker over its hidden map pin, and cards / pointers are styled by one
// generated stylesheet rule; nothing re-renders the list or rebuilds map data. The grown pin draws the map's own
// image at the map's size, opacity and position, and the map copy hides in the same frame, so there's no seam.
import * as maplibregl from "maplibre-gl";
import type * as GeoJSON from "geojson";
import { SHARED } from "../shared/config";
import { RULES } from "../shared/rules";
import { clockStore } from "../state/clock";
import { eventById } from "../state/feed";
import { selectedId } from "../state/view";
import { CLUSTER_RADIUS, GROUP_LAYER, PIN_LAYERS } from "./layers";
import type { MapController } from "./MapController";
import { SLIDE_LAYERS } from "./slide";

const HOVER = SHARED.hover;

const LAYERS = [...PIN_LAYERS, GROUP_LAYER];
const canHover = () => matchMedia("(hover: hover) and (pointer: fine)").matches && innerWidth > 760;

interface Pick { key: string; ids: Set<string>; cluster: number | null; marker: maplibregl.Marker | null; drawn: string; fromCard: boolean; group: boolean; timer?: ReturnType<typeof setTimeout> }
type What = { pin: string; feature: maplibregl.MapGeoJSONFeature } | { group: maplibregl.MapGeoJSONFeature } | { card: string } | null;

export class Hover {
  private base: Record<string, maplibregl.DataDrivenPropertyValueSpecification<number>> = {};
  private cur: Pick | null = null;
  private shown = new Map<string, Pick>();
  private dim = 1; private target = 1; private frame = 0;
  private rules = document.createElement("style");
  private mouse: { x: number; y: number } | null = null;
  private recheckFrame = 0;
  private cleanup: (() => void)[] = [];
  /** A zoom slide is drawing the grouped pins: the map's own stay hidden till it ends. */
  private slideHidden = false;

  constructor(private readonly ctl: MapController) {
    const map = ctl.map;
    document.documentElement.style.setProperty("--hover-ms", `${HOVER.ms}ms`);
    this.base = Object.fromEntries(LAYERS.map((l) => [l, (map.getPaintProperty(l, "icon-opacity") ?? 1) as maplibregl.DataDrivenPropertyValueSpecification<number>]));
    this.rules.id = "hover-rules";
    document.head.append(this.rules);
    let pending: maplibregl.Point | null = null;
    map.on("mousemove", (ev) => {
      if (!canHover()) return;
      pending = ev.point;
      requestAnimationFrame(() => {
        if (!pending) return;
        const f = map.queryRenderedFeatures([pending.x, pending.y], { layers: LAYERS })[0];
        pending = null;
        if (!f) { if (this.cur && !this.cur.fromCard) void this.pick(null, true); return; }
        if (f.layer.id === GROUP_LAYER) void this.pick({ group: f }, true);
        else void this.pick({ pin: String(f.properties?.id), feature: f }, true);
      });
    });
    map.getCanvas().addEventListener("mouseleave", () => { if (this.cur && !this.cur.fromCard) void this.pick(null, true); });
    const move = (ev: PointerEvent) => { this.mouse = ev.pointerType === "mouse" ? { x: ev.clientX, y: ev.clientY } : null; };
    const over = (ev: PointerEvent) => { if (ev.pointerType === "mouse") this.hoverAt(ev.target as Element); };
    const leave = () => { this.mouse = null; if (this.cur?.fromCard) void this.pick(null, false); };
    document.addEventListener("pointermove", move, { passive: true });
    document.addEventListener("pointerover", over);
    document.documentElement.addEventListener("pointerleave", leave);
    // The list moving under a still mouse (scrolling, re-rendering, the card opening over it): look again.
    const recheck = () => this.recheck();
    document.addEventListener("scroll", recheck, { capture: true, passive: true });
    // The list re-rendering, or the page's state classes changing (a sheet opening over the list): look again.
    const mo = new MutationObserver(recheck);
    const list = document.querySelector("#nearby");
    if (list) mo.observe(list, { childList: true, subtree: true });
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    // Moving the map or the data changing (groups renumber) drops the hover.
    map.on("movestart", () => void this.pick(null, false));
    this.cleanup.push(() => {
      document.removeEventListener("pointermove", move); document.removeEventListener("pointerover", over);
      document.documentElement.removeEventListener("pointerleave", leave); document.removeEventListener("scroll", recheck, { capture: true });
      mo.disconnect(); this.rules.remove();
    });
  }
  destroy() { this.cleanup.forEach((c) => c()); }
  hideForSlide(on: boolean) { this.slideHidden = on; this.paint(); }
  /** The data changed: groups renumber, so any hover on the map ends. */
  dataChanged() { if (this.cur && !this.cur.fromCard) void this.pick(null, false); }

  // ---------- the map: dim the rest, hide what the markers draw ----------
  private paint() {
    const map = this.ctl.map, hidePins: string[] = [], hideGroups: number[] = [];
    for (const e of this.shown.values()) { if (e.cluster != null) hideGroups.push(e.cluster); else hidePins.push(...e.ids); }
    for (const layer of LAYERS) {
      if (this.slideHidden && (SLIDE_LAYERS as readonly string[]).includes(layer)) { map.setPaintProperty(layer, "icon-opacity", 0); continue; }
      const own = this.base[layer] ?? 1;
      if (!this.cur && this.dim === 1 && !this.shown.size) { map.setPaintProperty(layer, "icon-opacity", own); continue; }
      const group = layer === GROUP_LAYER;
      const hidden = group ? ["in", ["get", "cluster_id"], ["literal", hideGroups]] : ["in", ["get", "id"], ["literal", hidePins]];
      const mine = group ? ["==", ["get", "cluster_id"], this.cur?.cluster ?? -1] : ["in", ["get", "id"], ["literal", this.cur ? [...this.cur.ids] : []]];
      // MapLibre's expression types are loose arrays (docs/ARCHITECTURE.md §2.1): built here, checked by the map.
      map.setPaintProperty(layer, "icon-opacity", ["case", hidden, 0, mine, own, ["*", own, this.dim]] as unknown as maplibregl.ExpressionSpecification);
    }
  }
  private animate() {
    if (this.frame) return;
    const from = this.dim, t0 = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / HOVER.ms);
      this.dim = from + (this.target - from) * k;
      this.paint();
      this.frame = k < 1 ? requestAnimationFrame(step) : 0;
      if (!this.frame && this.dim !== this.target) this.animate();
    };
    this.frame = requestAnimationFrame(step);
  }

  // ---------- the grown pin / group ----------
  private addMarker(entry: Pick, f: maplibregl.MapGeoJSONFeature) {
    const map = this.ctl.map, layer = f.layer.id, p = f.properties ?? {};
    entry.drawn = layer === GROUP_LAYER ? `g${p.cluster_id}` : `e${p.id}`;
    // One marker per pin or group: one still shrinking back grows again (never a second copy).
    const old = this.shown.get(entry.drawn);
    if (old?.marker) {
      clearTimeout(old.timer);
      entry.marker = old.marker; old.marker = null;
      this.shown.set(entry.drawn, entry);
      entry.marker.getElement().firstElementChild!.classList.add("hover-grow");
      this.paint();
      return;
    }
    const imageId = layer === GROUP_LAYER ? ["group", p.cluster_id, p.point_count, p.tone, p.best, p.free].join("|") : String(p.icon);
    const img = map.getImage(imageId);
    if (!img) return;
    const { width, height, data } = img.data, ratio = img.pixelRatio || 1;
    const c = document.createElement("canvas");
    c.width = width; c.height = height;
    c.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(data), width, height), 0, 0);
    c.style.width = `${width / ratio}px`; c.style.height = `${height / ratio}px`;
    const el = document.createElement("div"), inner = document.createElement("div");
    el.className = "pin-marker hover-marker"; inner.className = "pin-marker-inner";
    el.append(inner); inner.append(c);
    el.style.pointerEvents = "none"; // the map underneath keeps the hover and the click
    const size = Number(map.getLayoutProperty(layer, "icon-size") ?? 1);
    inner.style.setProperty("--hover-base", String(size));
    inner.style.setProperty("--hover-own", String(layer === GROUP_LAYER ? 1 : (layer === "secondary-pins" ? SHARED.pins.secondaryOpacity : 1) * Number(p.opacity ?? 1)));
    inner.style.setProperty("--hover-scale", String(HOVER.pinScale));
    // A still map draws an icon pixel for pixel when its image matches the screen's pixel ratio at icon-size 1: the
    // marker does the same, or it comes out blurred.
    const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
    const at = map.project(coords), dpr = devicePixelRatio || 1;
    const snap = ratio === dpr && size === 1 ? (v: number) => Math.round(v * dpr) / dpr - v : () => 0;
    entry.marker = new maplibregl.Marker({ element: el, subpixelPositioning: true, offset: [snap(at.x), snap(at.y)] }).setLngLat(coords).addTo(map);
    this.shown.set(entry.drawn, entry);
    this.paint(); // the map copy hides in the frame the marker first shows
    requestAnimationFrame(() => requestAnimationFrame(() => { if (this.cur === entry) inner.classList.add("hover-grow"); }));
  }
  private release(entry: Pick | null) {
    if (!entry?.marker) return;
    entry.marker.getElement().firstElementChild!.classList.remove("hover-grow");
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => {
      entry.marker?.remove();
      entry.marker = null;
      this.shown.delete(entry.drawn);
      this.paint();
    }, HOVER.ms + 20);
  }

  // ---------- cards and pointers: one generated rule ----------
  private styleRest() {
    const cur = this.cur;
    if (!cur) { this.rules.textContent = ""; document.body.classList.remove("hovering"); return; }
    document.body.classList.add("hovering");
    const now = clockStore.getState().now;
    const tone = (id: string) => { const e = eventById(id); return e && RULES.timeTone(e, now) === "now" ? "var(--now)" : "var(--soon)"; };
    const ids = [...cur.ids].map((id) => CSS.escape(id));
    const not = ids.map((id) => `[data-event="${id}"]`).join(",") || "[data-event='']";
    const notPointer = ids.map((id) => `[data-id="${id}"]`).join(",") || "[data-id='']";
    this.rules.textContent = [
      // A group on the map isn't one event: its cards get their outlines, but the others don't dim.
      ...(cur.group ? [] : [`body.hovering #nearby [data-mini]:not(${not}) { opacity: ${HOVER.dimOthers}; }`]),
      `body.hovering #pointers .pointer:not(${notPointer}) { filter: opacity(${HOVER.dimOthers}); }`,
      ...[...cur.ids].map((id) => `body.hovering #nearby [data-event="${CSS.escape(id)}"] { --hover-outline: ${tone(id)}; }`),
    ].join("\n");
  }
  private scrollToCard(id: string) {
    const card = document.querySelector<HTMLElement>(`#nearby [data-event="${CSS.escape(id)}"]`), list = document.querySelector("#nearby");
    if (!card || !list) return;
    const box = list.getBoundingClientRect(), r = card.getBoundingClientRect(), head = document.querySelector<HTMLElement>("#nearby-head")?.offsetHeight ?? 0;
    if (r.top < box.top + head || r.bottom > box.bottom) card.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  // ---------- picking ----------
  private async pick(what: What, fromMap: boolean) {
    const key = what ? ("group" in what ? `g${what.group.properties?.cluster_id}` : `e${"pin" in what ? what.pin : what.card}`) : null;
    if (key === (this.cur?.key ?? null)) return;
    this.release(this.cur);
    if (!what || !key) { this.cur = null; this.target = 1; this.styleRest(); this.animate(); return; }
    const mine: Pick = { key, ids: new Set(), cluster: null, marker: null, drawn: "", fromCard: !fromMap, group: "group" in what };
    this.cur = mine;
    const map = this.ctl.map, src = map.getSource("primary") as maplibregl.GeoJSONSource;
    const outline = () => { this.target = HOVER.dimOthers; this.styleRest(); this.animate(); };
    if ("group" in what) {
      mine.cluster = Number(what.group.properties?.cluster_id);
      this.addMarker(mine, what.group);
      const leaves = await src.getClusterLeaves(mine.cluster, Infinity, 0).catch(() => []);
      if (this.cur !== mine) return;
      for (const l of leaves) mine.ids.add(String(l.properties?.id));
    } else {
      const id = "pin" in what ? what.pin : what.card;
      if (!eventById(id)) return;
      mine.ids.add(id);
      const sel = selectedId();
      // A card whose pin is in a group: the group grows instead. The selected pin is already big (no marker).
      const f = "feature" in what ? what.feature : map.queryRenderedFeatures({ layers: [...PIN_LAYERS], filter: ["==", ["get", "id"], id] })[0];
      if (f && sel !== id) this.addMarker(mine, f);
      else if (!f && !fromMap && sel !== id) {
        outline(); // the card and the dimming don't wait for the group to be found
        const group = await this.groupOf(id);
        if (this.cur !== mine || !group) return;
        mine.cluster = Number(group.properties?.cluster_id);
        this.addMarker(mine, group);
        this.paint();
        return;
      }
    }
    outline();
    if (fromMap && !mine.group && mine.ids.size) this.scrollToCard([...mine.ids][0]!); // a group never scrolls the list
  }
  // The group holding an event, if one does: a group sits within two cluster radii of each of its members, so only
  // the groups drawn that near the event's spot can hold it (usually one). With several, the small ones are checked
  // first and the last one left is taken without reading its members, so a big group's thousands are never read.
  private async groupOf(id: string) {
    const map = this.ctl.map, e = eventById(id);
    if (!e) return null;
    const at = map.project([e.lng, e.lat]), reach = 2 * CLUSTER_RADIUS;
    const near = map.queryRenderedFeatures([[at.x - reach, at.y - reach], [at.x + reach, at.y + reach]], { layers: [GROUP_LAYER] })
      .filter((f, i, all) => all.findIndex((g) => g.properties?.cluster_id === f.properties?.cluster_id) === i)
      .sort((a, b) => Number(a.properties?.point_count) - Number(b.properties?.point_count));
    const src = map.getSource("primary") as maplibregl.GeoJSONSource;
    for (const [i, f] of near.entries()) {
      if (i === near.length - 1) return f;
      const n = Number(f.properties?.point_count);
      const leaves = await src.getClusterLeaves(Number(f.properties?.cluster_id), n, 0).catch(() => []);
      if (leaves.some((l) => l.properties?.id === id)) return f;
    }
    return null;
  }

  // ---------- the side panel: the card under the mouse, and nothing when it's over anything else ----------
  private hoverAt(target: Element | null) {
    if (!canHover()) return;
    const id = target?.closest?.("#nearby [data-mini]")?.getAttribute("data-event");
    if (id) void this.pick({ card: id }, false);
    else if (this.cur?.fromCard) void this.pick(null, false);
  }
  private recheck() {
    if (this.recheckFrame || !this.mouse) return;
    this.recheckFrame = requestAnimationFrame(() => {
      this.recheckFrame = 0;
      if (this.mouse) this.hoverAt(document.elementFromPoint(this.mouse.x, this.mouse.y));
    });
  }
}
